import { apiFetch } from './client';

export type FieldMode = 'read' | 'edit';

export interface RenderedField {
  fieldGid: string;
  label: string;
  mode: FieldMode;
  subtype: string;
  value: string | null;
  // Selected enum option gid + Asana palette colour, sent alongside `value`
  // (a display name) so the browser never matches options by name.
  valueGid?: string | null;
  valueColor?: string | null;
  // Available choices for an enum field (option gid + display name). Present
  // only when subtype is 'enum'.
  options?: Array<{ gid: string; name: string; color?: string }>;
}

export interface BoardNode {
  gid: string;
  name: string;
  level: 'view' | 'discuss';
  completed: boolean;
  isEpic: boolean;
  isMilestone: boolean;
  fields: RenderedField[];
  children: BoardNode[];
}

// The date span a section header advertises, from the section's epic-flagged
// tasks. Mirrors SectionRange in mi-portal-api/src/asana/tree.ts.
export interface SectionRange {
  start: string | null;
  due: string | null;
}

export interface BoardSection {
  gid: string | null;
  name: string;
  // Sections arrive in Asana board order, and every section on the board is
  // present — including ones with no visible tasks, which render as an empty
  // phase rather than being silently dropped.
  epicRange: SectionRange | null;
  nodes: BoardNode[];
}

// How the Board view splits into columns. Mirrors the server's GroupBy in
// mi-portal-api/src/asana/grouping.ts — keep the two in step.
export type GroupBy =
  | { key: 'section' }
  | { key: 'field'; fieldGid: string; label: string;
      options: Array<{ gid: string; name: string; color?: string }> };

// A dependency between two visible tasks. `from` blocks `to`.
export interface BoardEdge { from: string; to: string }

// One choice in the Raise an item dialog. The label is configured per client
// and is the only thing shown — the browser passes the GID straight back and
// never interprets it.
export interface RequestTypeChoice {
  optionGid: string;
  label: string;
}

export interface BoardResponse {
  configured: boolean;
  stale: boolean;          // served from cache because Asana was unreachable
  displayName: string;
  commentsEnabled: boolean;
  // Whether this client may raise an item. True only when the board is both
  // switched on for creation and has an intake section for it to land in —
  // the server ands the two, so the browser never offers a button the write
  // path would refuse.
  taskCreationEnabled: boolean;
  // What the client may classify a raised item as. Empty for a board
  // configured before request types existed, or one whose options have not
  // been set — the dialog then asks for a summary and detail only, and the
  // server stamps its configured default.
  requestTypes: RequestTypeChoice[];
  groupBy: GroupBy;
  edges: BoardEdge[];
  sections: BoardSection[];
}

export interface BoardComment {
  gid: string;
  author: string | null;
  text: string;
  createdAt: string;
  // 'field-update' is one of the portal's own audit records, returned only
  // when the board opts into showing field history.
  kind: 'comment' | 'field-update';
  // Present only for staff. A hidden comment is never sent to a client at
  // all, so this is never false-meaning-visible for them — it is absent.
  hidden?: boolean;
}

export interface TaskDetail {
  gid: string;
  name: string;
  level: 'view' | 'discuss';
  notes: string | null;
  fields: RenderedField[];
  comments: BoardComment[];
}

export interface AdminBoardConfig {
  workspaceGid: string;
  projectGid: string;
  visibilityFieldGid: string;
  viewOptionGid: string;
  discussOptionGid: string;
  epicFieldGid: string | null;
  epicOptionGid: string | null;
  displayName: string;
  commentsEnabled: boolean;
  showFieldHistory: boolean;
  boardGroupFieldGid: string | null;
  taskCreationEnabled: boolean;
  intakeSectionGid: string | null;
  requestTypeFieldGid: string | null;
  requestTypeQuestionOptionGid: string | null;
  // Optional on the wire, and absent is not the same as empty: the server
  // leaves stored options untouched when this is missing and replaces them
  // when it is present. A GET always returns it, so a normal load-edit-save
  // round trip carries them through unchanged.
  requestTypes?: Array<{ optionGid: string; label: string; sortOrder: number }>;
  fields: Array<{ fieldGid: string; label: string; mode: 'hidden' | 'read' | 'edit'; sortOrder: number }>;
}

const modBase = (slug: string) => `/clients/${slug}/modules/asana-board`;

export const getBoard = (slug: string, token: string | null) =>
  apiFetch<BoardResponse>(modBase(slug), token);

export const getTask = (slug: string, gid: string, token: string | null) =>
  apiFetch<TaskDetail>(`${modBase(slug)}/tasks/${gid}`, token);

export const postComment = (slug: string, gid: string, text: string, token: string | null) =>
  apiFetch<BoardComment>(`${modBase(slug)}/tasks/${gid}/comments`, token, {
    method: 'POST', body: JSON.stringify({ text }),
  });

export const createTask = (
  slug: string, name: string, notes: string, requestTypeOptionGid: string | null, token: string | null,
) =>
  apiFetch<{ gid: string; name: string }>(`${modBase(slug)}/tasks`, token, {
    // Omitted rather than sent as null when the board offers no types: the
    // server treats absence as "use the configured default" and rejects an
    // option it does not recognise, so there is nothing useful to say here.
    method: 'POST',
    body: JSON.stringify(requestTypeOptionGid ? { name, notes, requestTypeOptionGid } : { name, notes }),
  });

export const patchField = (slug: string, gid: string, fieldGid: string, value: string | null, token: string | null) =>
  apiFetch<{ ok: true }>(`${modBase(slug)}/tasks/${gid}`, token, {
    method: 'PATCH', body: JSON.stringify({ fieldGid, value }),
  });

// A field the project offers, for the admin picker. `editable` mirrors the
// server's write rules, so the UI never offers a mode the API would downgrade.
export interface AvailableField {
  fieldGid: string;
  name: string;
  subtype: string;
  builtin: boolean;
  editable: boolean;
  options?: Array<{ gid: string; name: string }>;
}

export const getAvailableFields = (clientId: string, projectGid: string, token: string | null) =>
  apiFetch<{ projectName: string | null; fields: AvailableField[] }>(
    `/admin/clients/${clientId}/asana-board/available-fields?projectGid=${encodeURIComponent(projectGid)}`,
    token,
  );

// A section of the project, in board order. `position` is 1-based.
export interface AvailableSection {
  gid: string;
  name: string;
  position: number;
  isLast: boolean;
}

export const getAvailableSections = (clientId: string, projectGid: string, token: string | null) =>
  apiFetch<{ sections: AvailableSection[] }>(
    `/admin/clients/${clientId}/asana-board/available-sections?projectGid=${encodeURIComponent(projectGid)}`,
    token,
  );

// Withhold (or restore) one comment for this client's board. Staff only —
// the route sits under /admin.
export const setCommentHidden = (
  clientId: string, storyGid: string, hidden: boolean, token: string | null,
) =>
  apiFetch<{ ok: true }>(
    `/admin/clients/${clientId}/asana-board/comments/${storyGid}/hide`,
    token,
    { method: 'POST', body: JSON.stringify({ hidden }) },
  );

export const getAdminConfig = (clientId: string, token: string | null) =>
  apiFetch<{ config: AdminBoardConfig | null }>(`/admin/clients/${clientId}/asana-board`, token);

export const putAdminConfig = (clientId: string, config: AdminBoardConfig, token: string | null) =>
  apiFetch<{ ok: true }>(`/admin/clients/${clientId}/asana-board`, token, {
    method: 'PUT', body: JSON.stringify(config),
  });
